import { useEffect, useLayoutEffect, useState } from 'react';

// The lib is also consumed by the Next apps, where layout effects don't run.
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * The natural height of a node, kept in sync while it changes.
 *
 * `CollapsibleComponent` animates `max-height`, which needs a number: `auto`
 * does not transition. The node is measured rather than guessed because its
 * content is rarely a fixed size - a date picker is one line on a desktop and
 * two once the label wraps, a step's body grows when a warning appears - and a
 * hardcoded height clips whatever does not fit.
 *
 * Measured in a layout effect so the first paint after mount already has the
 * real number; a plain effect leaves an open box collapsed for one frame.
 *
 * `ref` is a callback ref, so a node that mounts later than the component -
 * a page whose form only renders once its data has loaded - is still picked
 * up. An object ref read once on mount would find nothing and never measure.
 */
export const useMeasuredHeight = <Element extends HTMLElement>() => {
  const [element, ref] = useState<Element | null>(null);
  const [height, setHeight] = useState(0);

  useIsomorphicLayoutEffect(() => {
    if (!element) return;

    const measure = () => setHeight(element.clientHeight);
    measure();

    // An image that has not loaded yet takes up no room, so a node holding one
    // measures short until it does. ResizeObserver normally catches that, but
    // not in a tab the browser is not painting (a background tab, or one being
    // driven by the DevTools protocol), and the box then stays clipped.
    const pending = [...element.querySelectorAll('img')].filter(
      (img) => !img.complete
    );
    pending.forEach((img) => {
      img.addEventListener('load', measure);
      img.addEventListener('error', measure);
    });

    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    observer?.observe(element);

    return () => {
      pending.forEach((img) => {
        img.removeEventListener('load', measure);
        img.removeEventListener('error', measure);
      });
      observer?.disconnect();
    };
  }, [element]);

  return { ref, height };
};
